// Remembers the last dashboard offer and what we learned about its background
// images, so the festive Investing Essentials card can render straight away on
// a revisit or reload while `dashboard/offers` refreshes in the background.
//
// Kept in memory (in-app navigation) and in localStorage (reloads, app
// restarts). Every read is validated: a missing, corrupt, old-version or
// other-environment entry is ignored and the dashboard behaves like a first
// visit.
import { config } from "config";
import { localStore } from "stores/localStorage";

const CACHE_VERSION = 1;

// undefined = storage not read yet; null = nothing cached.
let memoryCache;
// Bumped on clear, so a request that was in flight during logout can't write
// the previous account's offer back.
let cacheEpoch = 0;
// Session only: images that already loaded in this tab, each held by an
// Image object so the browser keeps it decoded. A remounted <img> with the
// same URL and CORS mode then paints in its first frame.
const readyImages = new Map();

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const readStoredCache = () => {
  try {
    const stored = localStore.getDashboardOffer();
    if (
      !isPlainObject(stored) ||
      stored.v !== CACHE_VERSION ||
      stored.api !== config.apiUrl ||
      !isPlainObject(stored.offer)
    ) {
      return null;
    }
    return {
      ...stored,
      images: isPlainObject(stored.images) ? stored.images : {},
    };
  } catch (error) {
    return null;
  }
};

const getCache = () => {
  if (memoryCache === undefined) {
    memoryCache = readStoredCache();
  }
  return memoryCache;
};

const writeCache = (cache) => {
  memoryCache = cache;
  try {
    localStore.setDashboardOffer(cache);
  } catch (error) {
    // Storage full or blocked: the in-memory copy still covers this session.
  }
};

const offerImageUrls = (offer) =>
  [offer?.essentialsBgWeb, offer?.essentialsBgMobile].filter(Boolean);

export const getCacheEpoch = () => cacheEpoch;

export const getCachedOffer = () => getCache()?.offer ?? null;

// Stores a freshly fetched offer. Image facts are kept only for the offer's
// current URLs, so storage doesn't grow from month to month.
export const saveCachedOffer = (offer) => {
  const previous = getCache();
  const urls = offerImageUrls(offer);
  const images = {};
  urls.forEach((url) => {
    if (previous?.images?.[url]) {
      images[url] = previous.images[url];
    }
  });
  readyImages.forEach((_, url) => {
    if (!urls.includes(url)) readyImages.delete(url);
  });
  writeCache({ v: CACHE_VERSION, api: config.apiUrl, offer, images });
};

// { aspect, noCors, titleColor: { desktop, mobile } } for a background URL.
export const getBgMeta = (url) => {
  const meta = url ? getCache()?.images?.[url] : null;
  if (!isPlainObject(meta)) return null;
  const aspect = Number(meta.aspect);
  const titleColor = isPlainObject(meta.titleColor) ? meta.titleColor : {};
  return {
    aspect: Number.isFinite(aspect) && aspect > 0 ? aspect : null,
    noCors: meta.noCors === true,
    titleColor: {
      desktop:
        typeof titleColor.desktop === "string" ? titleColor.desktop : null,
      mobile: typeof titleColor.mobile === "string" ? titleColor.mobile : null,
    },
  };
};

export const updateBgMeta = (url, patch = {}) => {
  const cache = getCache();
  if (!url || !cache || !offerImageUrls(cache.offer).includes(url)) return;
  const current = isPlainObject(cache.images[url]) ? cache.images[url] : {};
  const next = {
    ...current,
    ...patch,
    titleColor: { ...current.titleColor, ...patch.titleColor },
  };
  if (JSON.stringify(next) === JSON.stringify(current)) return;
  writeCache({ ...cache, images: { ...cache.images, [url]: next } });
};

export const markBgReady = (url, crossOrigin) => {
  if (!url || readyImages.has(url)) return;
  const keeper = new Image();
  if (crossOrigin) keeper.crossOrigin = crossOrigin;
  keeper.src = url;
  readyImages.set(url, keeper);
};

export const isBgReady = (url) => Boolean(url) && readyImages.has(url);

export const clearDashboardOfferCache = () => {
  cacheEpoch += 1;
  memoryCache = null;
  readyImages.clear();
  try {
    localStore.resetDashboardOffer();
  } catch (error) {
    // Nothing to clear when storage is blocked.
  }
};
